// Push Notifications Utility
// Handles Web Push API for saved searches notifications

import { API_BASE } from './supabase/info';

const BACKEND_URL = API_BASE;

// Request notification permission and subscribe to push notifications
export async function requestPushNotificationPermission(accessToken: string): Promise<boolean> {
  try {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }

    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      console.log('Browser does not support service workers');
      return false;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return false;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key from backend (same endpoint as SettingsPage / pushV2)
    const vapidResponse = await fetch(`${BACKEND_URL}/vapid-public-key`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const { publicKey } = await vapidResponse.json();

    if (!publicKey) {
      console.error('Failed to get VAPID public key');
      return false;
    }

    // Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
    });

    // Send subscription to backend (same endpoint as SettingsPage / pushV2)
    const subscribeResponse = await fetch(`${BACKEND_URL}/push-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(subscription.toJSON()),
    });

    if (!subscribeResponse.ok) {
      console.error('Failed to subscribe to push notifications');
      return false;
    }

    console.log('✅ Successfully subscribed to push notifications');
    return true;
  } catch (error) {
    console.error('Error requesting push notification permission:', error);
    return false;
  }
}

// Unsubscribe from push notifications
export async function unsubscribePushNotifications(accessToken: string): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      console.log('No push subscription found');
      return true;
    }

    // Unsubscribe from backend (same endpoint as SettingsPage / pushV2)
    await fetch(`${BACKEND_URL}/push-subscription`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });

    // Unsubscribe locally
    await subscription.unsubscribe();

    console.log('✅ Successfully unsubscribed from push notifications');
    return true;
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    return false;
  }
}

// Check if user is subscribed to push notifications
export async function isPushNotificationSubscribed(): Promise<boolean> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    return subscription !== null;
  } catch (error) {
    console.error('Error checking push subscription:', error);
    return false;
  }
}

// Helper: Convert VAPID public key to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Helper: Convert ArrayBuffer to Base64
function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
